import { sortBy } from 'lodash';
import type { RiskAssessmentViewModel, SubstanceViewModel } from '@trinnvis/dabih-api-client';
import { BASE_PATH } from '@trinnvis/dabih-api-client';
import type { ApiClient } from 'src/store/backend.js';
import { attachment } from 'src/store/backend.js';
import type { SuggestedRiskAssessment } from 'src/store/selectors';
import {
  getOrganization,
  key,
  partnersNotDeleted,
  riskAssessmentsNotDeleted,
  suggestedRiskAssessmentsForSubstances,
  toSubstanceViewModel,
  writeAccess,
} from 'src/store/selectors';
import type { State } from 'src/store/types.js';
import { LocalDate } from 'src/utilities/local-date.js';
import { entityNameForLists } from 'src/utilities/text';
import { getPromoDataForEntity } from 'src/store/selectors/promo';
import type { RelatedRiskAssessment } from '../../library/lists/related-risk-assessment';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import type { SubstanceViewEditItem } from '../../content/substances/substance-view-edit-item';
import type { SubstanceView } from '../../content/substances/substance-view';
import { assertIsDefined } from '../../assertIsDefined';
import { assertEntityFound } from './entity-not-found';
import { toAttachmentItems } from '../to-attachment-items';
import { fetchDraft } from '../fetch-draft';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';
import { checkCondition } from 'src/store/selectors/conditional/conditional-check';
import { organizationDataForSuggestions } from 'src/store/selectors/conditional/organization-data-for-suggestions';
import { t } from 'src/i18n';

function suggestedRiskAssessmentsForSubstance(
  organization: any,
  suggestedRiskAssessments: SuggestedRiskAssessment[],
  substanceUuid: string,
): SuggestedRiskAssessment[] {
  return suggestedRiskAssessments.filter((s) => {
    return checkCondition(organization, s.condition.expression, s.condition.context, undefined, substanceUuid);
  });
}

function _relatedRiskAssessments(
  substance: SubstanceViewModel,
  suggestedRiskAssessments: SuggestedRiskAssessment[],
  riskAssessments: RiskAssessmentViewModel[],
): RelatedRiskAssessment[] {
  const result: RelatedRiskAssessment[] = [];
  suggestedRiskAssessments.forEach((item) => {
    const createdFromTemplate = riskAssessments.find(
      (r) => r.relatedSubstance === substance.uuid && r.createdFromTemplate === item.pageId,
    );
    if (createdFromTemplate) {
      const c = createdFromTemplate;
      let risk: number | null = null;
      if (c.probability && c.consequence) {
        risk = c.probability * c.consequence;
      }
      result.push({
        suggestion: false,
        uuid: c.uuid,
        name: entityNameForLists(c.name, c.draftName, c.isConfirmedEntity),
        risk: risk ?? 0,
        hasDraft: c.hasDraft,
      });
    } else {
      result.push({
        suggestion: true,
        templateId: item.pageId,
        name: item.name,
        risk: 0,
        uuid: '',
        hasDraft: false,
      });
    }
  });
  riskAssessments = sortBy(riskAssessments, (item) => item.name);
  riskAssessments.forEach(function (r) {
    const s = suggestedRiskAssessments.filter(function (item) {
      return item.pageId === r.createdFromTemplate;
    });
    if (r.relatedSubstance === substance.uuid && (!r.createdFromTemplate || s.length === 0)) {
      let risk: any = null;
      if (r.probability && r.consequence) {
        risk = r.probability * r.consequence;
      }
      result.push({
        suggestion: false,
        uuid: r.uuid,
        name: entityNameForLists(r.name, r.draftName, r.isConfirmedEntity),
        risk: risk,
        hasDraft: r.hasDraft,
      });
    }
  });
  return result;
}

export function buildSubstanceView(
  api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): SubstanceView {
  const organization = getOrganization(state);
  assertIsDefined(organization);
  const item = organization.substancesById[uuid];
  assertEntityFound(item, 'substances', uuid);
  const entity = toSubstanceViewModel(item);
  const riskAssessments = riskAssessmentsNotDeleted(state);
  const suggestedRiskAssessments: SuggestedRiskAssessment[] = suggestedRiskAssessmentsForSubstance(
    organizationDataForSuggestions(state),
    suggestedRiskAssessmentsForSubstances(),
    item.uuid,
  );
  const partnerOptions = partnersNotDeleted(state)
    .filter((p) => p.isConfirmedEntity)
    .map((p) => ({ value: p.uuid, text: p.name }));
  partnerOptions.push({ value: 'NEW', text: t('substancesView.newSupplier') });
  return {
    ...commonData,
    promoData: getPromoDataForEntity('substances', item, getFeatureStates(state)),
    docsForLinking: [],
    currentUserHasAccess: true,
    currentUserHasWriteAccess: writeAccess(state),
    uuid: uuid,
    isConfirmedEntity: entity.isConfirmedEntity,
    helpContent: '',
    href: currentParent + '/substances/' + uuid,
    pdfHref:
      BASE_PATH + '/organizations/' + organization.organizationId + '/substances/' + uuid + '.pdf?key=' + key(state),
    parentHref: currentParent,
    deleted: entity.deleted === true,
    type: 'substances',
    name: entity.name ?? '',
    attachments: toAttachmentItems(api, 'substances', entity.uuid, entity.attachments),
    location: entity.location ?? '',
    supplierName: partnerOptions.find((p) => p.value === entity.supplierUuid)?.text ?? '',
    notes: entity.notes ?? '',
    applications: entity.applications ?? '',
    archived: entity.archived === true,
    hazardLabels: entity.hazardLabels ?? [],
    relatedRiskAssessments: _relatedRiskAssessments(entity, suggestedRiskAssessments, riskAssessments),
    supplierUuid: entity.supplierUuid ?? '',
    today: LocalDate.fromString(state.today),
    partnerOptions: partnerOptions,
    pdf: fetchAttachment(
      api,
      organization.organizationId,
      entity.uuid,
      (entity.attachments ?? [])[0].uuid,
      (entity.attachments ?? [])[0]?.urlForInline,
    ),
    unreadable: entity.uploadStatus === 'UNREADABLE',
    deletable: true,
    hasDraft: entity.hasDraft,
    fetchDraft: async () => {
      const command = await fetchDraft(api, 'substances', uuid, organization.organizationId);
      return command.draft as SubstanceViewEditItem;
    },
  };
}

async function fetchAttachment(
  api: ApiClient,
  organizationId: number,
  substanceUuid: string,
  attachmentUuid: string,
  offlineFallbackUrl?: string,
): Promise<string> {
  if (!navigator.onLine && offlineFallbackUrl) {
    return offlineFallbackUrl;
  }
  const a = await attachment(api, 'substances', substanceUuid, organizationId, attachmentUuid);
  return a.urlForInline ?? '';
}
